import { useRealtimeConnection, ConnectionStatus } from "@/hooks/useRealtimeConnection";
import { Wifi, WifiOff, Loader2, RefreshCw } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getActiveChannelCount } from "@/lib/supabase-channel";
import { useState, useEffect } from "react";

const statusConfig: Record<
  ConnectionStatus,
  { icon: React.ElementType; label: string; className: string; dotClass: string }
> = {
  connecting: {
    icon: Loader2,
    label: "Conectando...",
    className: "text-yellow-500",
    dotClass: "bg-yellow-500 animate-pulse",
  },
  connected: {
    icon: Wifi,
    label: "Conectado em tempo real",
    className: "text-green-500",
    dotClass: "bg-green-500",
  },
  disconnected: {
    icon: WifiOff,
    label: "Desconectado",
    className: "text-destructive",
    dotClass: "bg-destructive animate-pulse",
  },
};

export function RealtimeIndicator() {
  const { status } = useRealtimeConnection();
  const config = statusConfig[status];
  const Icon = config.icon;
  const [channelCount, setChannelCount] = useState(0);

  // Update channel count periodically
  useEffect(() => {
    const updateCount = () => setChannelCount(getActiveChannelCount());
    updateCount();
    const interval = setInterval(updateCount, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/50 border border-border/50 cursor-default">
            <span className={cn("relative flex h-2 w-2", config.dotClass, "rounded-full")}>
              {status === "connected" && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
              )}
            </span>
            <Icon
              className={cn(
                "h-4 w-4",
                config.className,
                status === "connecting" && "animate-spin"
              )}
            />
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {status === "connected" ? "Online" : status === "connecting" ? "Conectando" : "Offline"}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-sm">
            <p className="font-medium">{config.label}</p>
            <p className="text-xs text-muted-foreground">
              {channelCount} {channelCount === 1 ? "canal" : "canais"} ativos
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
